import { Required } from 'utility-types';
import { TeamMemberDataType } from '../../team/view/components/TeamMembersTable';
import { OrganisationMemberDataType } from './OrganisationMembersTable';
import {
	GetOrganisationQuery,
	UserInfoFragment,
	useGetOrganisationQuery,
} from './__generated__/organisation.generated';
import { useNewMemberSubscription } from './__generated__/newMember.subscription.generated';

export const useOrganisationData = ({ uuid }: { uuid: string }) => {
	const { loading, error, data, refetch } = useGetOrganisationQuery({ variables: { uuid } });
	useNewMemberSubscription({ variables: { uuid }, onData: () => refetch() });

	if (!data) {
		return { loading, error, organisation: null };
	}

	const { members, teams, temporaryAccesses } = data.organisation;
	const membersData = getDataForTables(members);
	const teamsData = teams.map((team) => ({
		...team,
		members: getTeamMembersDataForTables(team.members),
	}));

	const temporaryAccessData = getDataForTemporaryAccessTable(temporaryAccesses);

	return {
		loading,
		error,
		organisation: {
			...data.organisation,
			members: membersData,
			teams: teamsData,
			temporaryAccess: temporaryAccessData,
		},
	};
};

const getTeamMembersDataForTables = (
	members: GetOrganisationQuery['organisation']['teams'][0]['members'],
) =>
	members.reduce<{
		caregivers: TeamMemberDataType[];
		careRecipients: TeamMemberDataType[];
		archived: TeamMemberDataType[];
	}>(
		(tableData, member) => {
			const { role, organisationMember } = member;
			const { archivedAt, user, id } = organisationMember;
			const userForTable = { ...user, teamRole: role, memberId: id };

			if (archivedAt) {
				return {
					...tableData,
					archived: [...tableData.archived, userForTable],
				};
			}

			if (user.role === 'PAL') {
				return {
					...tableData,
					caregivers: [...tableData.caregivers, userForTable],
				};
			}
			if (user.role === 'USER') {
				return {
					...tableData,
					careRecipients: [...tableData.careRecipients, userForTable],
				};
			}

			return tableData;
		},
		{ caregivers: [], careRecipients: [], archived: [] },
	);

type NonNullableField<T, K extends keyof T> = Omit<T, K> &
	Required<{ [P in K]: Exclude<T[P], null> }>;

type OrganisationTableMemberDataType = Omit<OrganisationMemberDataType, 'guardians' | 'superPals'>;
type OrganisationTableFamilyCaregiverDataType = NonNullableField<
	Omit<OrganisationTableMemberDataType, 'organisationRole'>,
	'guardianOf'
>;
const getDataForTables = (members: GetOrganisationQuery['organisation']['members']) => {
	const usersForTable = members.reduce<{
		caregivers: OrganisationTableMemberDataType[];
		familyCaregivers: OrganisationTableFamilyCaregiverDataType[];
		careRecipients: OrganisationTableMemberDataType[];
		archived: OrganisationTableMemberDataType[];
	}>(
		(tableData, member) => {
			const { archivedAt, role, user, id } = member;
			const { guardians, ...userForTable } = { ...user, organisationRole: role, memberId: id };

			if (archivedAt) {
				return {
					...tableData,
					archived: [...tableData.archived, userForTable],
				};
			}

			if (user.role === 'PAL') {
				return {
					...tableData,
					caregivers: [...tableData.caregivers, userForTable],
				};
			}

			if (user.role === 'USER') {
				return {
					...tableData,
					careRecipients: [...tableData.careRecipients, userForTable],
					familyCaregivers: [
						...tableData.familyCaregivers,
						...guardians
							.filter((guardian) => !members.some((member) => member.user.id === guardian.id))
							.map((familyGuardian) => ({
								...familyGuardian,
								guardianOf: [user],
								memberId: id,
							})),
					],
				};
			}

			return tableData;
		},
		{ caregivers: [], familyCaregivers: [], careRecipients: [], archived: [] },
	);

	const familyCaregiversHashmap = usersForTable.familyCaregivers.reduce(
		(familyCaregivers, careGiver) => {
			familyCaregivers[careGiver.id]
				? familyCaregivers[careGiver.id].guardianOf.push(...careGiver.guardianOf)
				: (familyCaregivers[careGiver.id] = { ...careGiver });

			return familyCaregivers;
		},
		{} as {
			[key: string]: OrganisationTableFamilyCaregiverDataType;
		},
	);
	const familyCaregivers = Object.values(familyCaregiversHashmap);

	return { ...usersForTable, familyCaregivers };
};

export type TemporaryAccessTableDataType = {
	careRecipient: UserInfoFragment;
	endDate: number;
} & (
	| {
			type: 'temporaryConnection';
			caregiver: UserInfoFragment;
			temporaryConnectionUuid: string;
	  }
	| {
			type: 'appInvitation';
			caregiver: { email: string };
			appInvitationId: string;
	  }
);

const getDataForTemporaryAccessTable = ({
	connections,
	appInvitations,
}: GetOrganisationQuery['organisation']['temporaryAccesses']): TemporaryAccessTableDataType[] => {
	const temporaryCaregiversData = connections.map((connection) => ({
		caregiver: connection.caregiver,
		endDate: connection.endDate,
		type: 'temporaryConnection' as const,
		careRecipient: connection.careRecipient,
		temporaryConnectionUuid: connection.uuid,
	}));

	const appInvitationsData = appInvitations.map((appInvitation) => ({
		caregiver: { email: appInvitation.toEmail },
		endDate: appInvitation.connectionEndDate!,
		type: 'appInvitation' as const,
		careRecipient: appInvitation.careReceiverToConnect,
		appInvitationId: appInvitation.id,
	}));

	return [...temporaryCaregiversData, ...appInvitationsData];
};

export const splitForCurrentTeamAndRest = <Team extends { uuid: string }>(
	teams: Team[],
	uuid: string,
) => {
	return teams.reduce<{
		currentTeam: Team | null;
		otherTeams: Team[];
	}>(
		(allTeams, team) => {
			if (team.uuid === uuid) {
				return {
					...allTeams,
					currentTeam: team,
				};
			}

			return {
				...allTeams,
				otherTeams: [...allTeams.otherTeams, team],
			};
		},
		{
			currentTeam: null,
			otherTeams: [],
		},
	);
};
